// TEMPORARY — Zendesk cutover tool. Bulk CSV upload of organizations and contacts.
// Removed from the Admin landing page; route still wired in App.tsx for direct access.
// Delete this file, its route, and the backend endpoint together once cutover is done.

import { useState } from 'react';
import { Link } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { useToast } from '../components/Toaster.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface ImportResult {
  imported: number;
  skipped: number;
  scanned: number;
  failures: Array<{ row: number; name?: string; email?: string; error: string }>;
}

export function AdminBulkImportPage(): React.ReactElement {
  useDocumentTitle('Bulk import');
  const { agent } = useAuth();
  const toast = useToast();
  const [orgFile, setOrgFile] = useState<File | null>(null);
  const [contactFile, setContactFile] = useState<File | null>(null);
  const [orgResult, setOrgResult] = useState<ImportResult | null>(null);
  const [contactResult, setContactResult] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState<'orgs' | 'contacts' | null>(null);

  if (!agent || agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  async function uploadOrgs(e: React.FormEvent) {
    e.preventDefault();
    if (!orgFile) return;
    setBusy('orgs');
    try {
      const fd = new FormData();
      fd.append('file', orgFile, orgFile.name);
      const res = await apiFetch<ImportResult>('/api/admin/bulk-import/organizations', {
        method: 'POST',
        body: fd,
      });
      setOrgResult(res);
      toast.success(`Imported ${res.imported} organizations (${res.skipped} skipped).`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Org import failed.');
    } finally {
      setBusy(null);
    }
  }

  async function uploadContacts(e: React.FormEvent) {
    e.preventDefault();
    if (!contactFile) return;
    setBusy('contacts');
    try {
      const fd = new FormData();
      fd.append('file', contactFile, contactFile.name);
      const res = await apiFetch<ImportResult>('/api/admin/bulk-import/contacts', {
        method: 'POST',
        body: fd,
      });
      setContactResult(res);
      toast.success(`Imported ${res.imported} contacts (${res.skipped} skipped).`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Contact import failed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-1 text-2xl font-bold text-[var(--color-header)]">Bulk import (CSV)</h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Upload CSV files of organizations and contacts. Re-runs are safe — duplicates by org name
          or contact email are skipped.
        </p>
      </div>

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">1. Organizations</h2>
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Required column: <code>name</code>. Optional: <code>domain</code>, <code>timezone</code>,{' '}
          <code>created_at</code> (preserves "client since" date when migrating).
        </p>
        <pre className="mt-2 overflow-auto rounded-[var(--radius-button)] bg-[var(--color-surface)] p-2 text-xs">
{`name,domain,timezone,created_at
AcmeCorp,acmecorp.com,America/New_York,2022-03-15
Globex,globex.com,America/Chicago,`}
        </pre>
        <form onSubmit={uploadOrgs} className="mt-4 flex flex-wrap items-end gap-3">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setOrgFile(e.target.files?.[0] ?? null)}
            className="text-sm file:mr-3 file:rounded-[var(--radius-button)] file:border-0 file:bg-[var(--color-t3-blue)] file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white"
          />
          <Button type="submit" disabled={busy !== null || !orgFile}>
            {busy === 'orgs' ? 'Importing…' : 'Upload organizations'}
          </Button>
        </form>
        {orgResult && (
          <div className="mt-3 text-sm">
            <p>Scanned {orgResult.scanned} · imported <strong>{orgResult.imported}</strong> · skipped {orgResult.skipped}.</p>
            {orgResult.failures.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-[var(--color-danger)]">{orgResult.failures.length} failures</summary>
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {orgResult.failures.map((f, i) => (
                    <li key={i}>row {f.row} ({f.name}): {f.error}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </Card>

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">2. Contacts</h2>
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Required columns: <code>organization</code> (must match an org name exactly), <code>email</code>.
          Optional: <code>name</code>, <code>role</code> (requester | org_admin | org_billing; defaults to requester),{' '}
          <code>created_at</code> (preserves original signup date).
        </p>
        <pre className="mt-2 overflow-auto rounded-[var(--radius-button)] bg-[var(--color-surface)] p-2 text-xs">
{`organization,email,name,role,created_at
AcmeCorp,jane@acmecorp.com,Jane Doe,requester,2023-01-10
AcmeCorp,billing@acmecorp.com,Acme Billing,org_billing,
Globex,alex@globex.com,Alex Smith,org_admin,2024-06-01`}
        </pre>
        <form onSubmit={uploadContacts} className="mt-4 flex flex-wrap items-end gap-3">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setContactFile(e.target.files?.[0] ?? null)}
            className="text-sm file:mr-3 file:rounded-[var(--radius-button)] file:border-0 file:bg-[var(--color-t3-blue)] file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white"
          />
          <Button type="submit" disabled={busy !== null || !contactFile}>
            {busy === 'contacts' ? 'Importing…' : 'Upload contacts'}
          </Button>
        </form>
        {contactResult && (
          <div className="mt-3 text-sm">
            <p>Scanned {contactResult.scanned} · imported <strong>{contactResult.imported}</strong> · skipped {contactResult.skipped}.</p>
            {contactResult.failures.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-[var(--color-danger)]">{contactResult.failures.length} failures</summary>
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {contactResult.failures.map((f, i) => (
                    <li key={i}>row {f.row} ({f.email}): {f.error}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
